import { getCaSettings } from "../../../../../builders/settings/CaSetting";
import { headerSettings } from "./tableSettings";

export const getCaColumnGuide = (
  caType,
  caSettings = getCaSettings(),
  isHirePurchase = false
) => {
  const isS19D = caType === "S19D";
  const settings = (
    Array.isArray(caSettings) && caSettings.length
      ? caSettings
      : getCaSettings()
  ).filter((setting) => setting.caType === caType);
  const descriptions = {
    "CA Name": {
      description:
        "The type of capital allowance you are claiming for the asset.",
      details: [
        {
          label: "Available options",
          text: settings.map((setting) => setting.key).join(", ") || caType,
        },
      ],
    },
    "CA Summary": {
      description: "The allowance category this asset belongs to.",
    },
    "Allowance Type": {
      description: "Whether you are claiming the base or enhanced allowance.",
      details: [
        {
          label: "What this means",
          text: settings.some((setting) => setting.enhancedAllowance)
            ? "Base is the standard allowance. Enhanced includes the extra allowance available under the selected CA option."
            : "Base is the standard allowance for the selected CA option.",
        },
      ],
    },
    "IA Rate (%)": {
      description:
        "The rate used to work out Initial Allowance (IA) in the YA of purchase.",
    },
    "No. of years of claim": {
      description: isS19D
        ? "The original claim period. Normally, annual WDA is the original cost divided by this number of years."
        : "How many years you claim the allowance over. The period depends on the CA option.",
    },
    "No. of years b/f": {
      description: "How many years of claims are left at the start of this YA.",
      ...(isS19D && {
        note: "For a partial disposal, use the number of complete years left to work out WDA on the remaining balance.",
      }),
    },
    "Principal Paid": {
      description:
        "The amount repaid towards the asset's purchase price for the period, excluding interest.",
      details: [
        {
          label: "Hire purchase claims",
          text: "For S19, IA and AA follow the Hire Purchase schedule. Other CA types use their own allowance rules.",
        },
      ],
    },
    Cost: {
      description:
        "What the asset originally cost. This is the starting point for normal S19D writing-down allowance (WDA).",
      note: "Keep the original cost even if you sell part of the asset.",
    },
    "Asset in use at FYE": {
      description:
        "Whether the asset was being used for business at financial year end (FYE).",
      details: [
        {
          label: "If No",
          text: "There is no WDA claim for this YA. The number of claim years left still falls by one.",
        },
      ],
    },
    "TWDV b/f": {
      description:
        "The asset's remaining tax value at the start of this YA, before this year's claims or disposals.",
    },
    Additions: {
      description: isHirePurchase
        ? "The amount for this YA's hire purchase claim. For S19, IA and AA follow the Hire Purchase schedule."
        : "The cost of assets added during this YA.",
    },
    "Prior Year Disposal": {
      description: isS19D
        ? "The tax value of assets bought in an earlier YA and disposed of this year."
        : "The tax value deducted for assets bought in an earlier YA and disposed of this year.",
      ...(isS19D && {
        note: "For a partial disposal, WDA adjustment uses the Prior Year Disposal amount, based on TWDV b/f in FA Disposal.",
      }),
    },
    "Current Year Disposal": {
      description:
        "The tax value deducted for assets bought and disposed of in the same YA. For deferred claims, this amount is zero.",
    },
    Total: {
      description:
        "The tax value left before this year's allowances: opening TWDV plus additions, less disposals.",
      ...(isS19D && {
        details: [
          {
            label: "After a partial disposal",
            text: "Opening TWDV minus Prior Year Disposal. The remaining balance cannot be below zero.",
          },
        ],
      }),
    },
    Deferred: {
      description:
        "Skip this YA's allowance claim and carry the balance forward.",
      note: isS19D
        ? "The claim period still shortens by one year, even when you defer the claim."
        : "The years left stay the same when you defer a claim.",
    },
    Disregard: {
      description:
        "Excludes part of your S16 Initial Allowance and Annual Allowance from the claim.",
    },
    "Disregard Rate (%)": {
      description:
        "How much of the S16 IA and AA to leave out. For example, 20% means claiming only the remaining 80%.",
    },
    "Initial Allowance (IA)": {
      description: isHirePurchase
        ? "The Initial Allowance for S19 hire purchase assets. There is no claim this YA if you defer it."
        : "The allowance for the YA of purchase, worked out using the IA rate and Total. There is no claim this YA if you defer it.",
    },
    "Annual Allowance (AA)": {
      label: isS19D ? "Annual Allowance (AA) / WDA" : "Annual Allowance (AA)",
      description: isS19D
        ? "Your yearly writing-down allowance (WDA): original cost divided by the total claim years. It is rounded to whole dollars and cannot exceed the tax value left."
        : caType === "S19A"
          ? "The yearly claim under your S19A option. It may be spread evenly, follow a percentage split, or be claimed in one year. A limit may apply."
          : isHirePurchase && caType === "S19"
            ? "The yearly allowance for your hire purchase asset. There is no claim this YA if the allowance is deferred or the asset has been disposed of."
            : caType === "S19B"
              ? "The yearly claim spread over the remaining claim years. Any enhanced allowance is worked out separately."
              : "The yearly claim on the balance left after IA, spread over the remaining years or using the applicable percentage split.",
      ...(isS19D && {
        details: [
          {
            label: "After partial disposal",
            text: "Spread the tax value left after the sale over the remaining claim years. You cannot claim more than the tax value left.",
          },
          {
            label: "No current-year claim",
            text: "There is no WDA claim if you defer it, the asset is not in use at FYE, or no claim years or tax value remain.",
          },
        ],
      }),
    },
    "TWDV c/f": {
      description: isS19D
        ? "The tax value left after this YA's WDA claim. This is the opening tax value for the next YA."
        : ["S19", "S16"].includes(caType)
          ? "The tax value left after this YA's IA and AA claims. This is the opening tax value for the next YA."
          : "The tax value left after this YA's AA claim. This is the opening tax value for the next YA.",
      note: "If you defer the claim, carry the balance forward without deducting this YA's allowance.",
    },
    "No. of years c/f": {
      description: isS19D
        ? "The years left after this YA: years b/f minus one, down to a minimum of zero. This also applies if you defer the claim or the asset is not in use at FYE."
        : "The years left after this YA: years b/f minus one, down to a minimum of zero. The years stay the same if you defer the claim.",
    },
  };

  const columns = headerSettings
    .filter((header) => header.visibility.includes(caType))
    .filter((header) =>
      isHirePurchase
        ? header.showInHirePurchase !== false && header.key !== "isDeferred"
        : header.showInHirePurchase !== true && header.key !== "principalPaid"
    )
    .map((header) => ({
      id: header.value,
      label: header.value,
      ...descriptions[header.value],
    }));

  columns.unshift({
    id: "ya",
    label: "YA",
    description:
      "The Year of Assessment (YA) for the asset's purchase.",
  });

  if (settings.some((setting) => setting.enhancedAllowance)) {
    columns.push({
      id: "enhanced",
      label: "Enhanced allowance",
      description:
        "The extra allowance available under the selected CA option.",
      details: [
        {
          label: "Enhanced amount",
          text: "The qualifying amount, up to the applicable limit, multiplied by the enhanced allowance percentage.",
        },
        {
          label: "Base and enhanced claims",
          text: "The enhanced allowance and its remaining tax value are worked out separately from the base claim.",
        },
      ],
    });
  }

  if (isS19D) {
    columns.splice(2, 0, {
      id: "wda-adjustment",
      label: "WDA adjustment for partial disposal",
      description:
        "Use this when you sell part of an asset and continue claiming WDA on the part you keep.",
      details: [
        {
          label: "Disposal amount",
          text: "Use the Prior Year Disposal amount, based on TWDV b/f in FA Disposal. This reduces the tax value available for future WDA.",
        },
        {
          label: "Disposal amount below opening TWDV",
          text: "WDA = (opening TWDV - Prior Year Disposal) / complete claim years remaining at the start of the YA.",
        },
        {
          label: "Disposal amount at or above opening TWDV",
          text: "There is no WDA left to claim. Any excess is a balancing charge (BC), limited to WDA already claimed less any previous balancing charges.",
        },
        {
          label: "Next YA",
          text: "The revised WDA continues over the remaining claim period.",
        },
      ],
      example:
        "Opening TWDV $8,000 - Prior Year Disposal $2,000 = $6,000. With 2 years left, WDA is $3,000 a year and TWDV c/f is $3,000 after this year's claim.",
      note: "Work out a partial disposal for one asset at a time. Check the original cost and the complete claim years left.",
    });
  }

  return columns;
};
